import type {
  User,
  Users,
} from "@bezoned/shared/fluid/office-schema-factory.js";
import { Tree } from "fluid-framework";
import type { TreeNode } from "fluid-framework";
import { useEffect, useRef, useState } from "react";

import { useFluid } from "../features/fluid/hooks/use-fluid.js";

interface AvatarSeatingParams {
  /**
   * The Fluid node for this location (table or room).
   * The hook subscribes to changes on this node.
   */
  locationNode: TreeNode | null | undefined;
  /**
   * Returns the current participant IDs at this location.
   * Called inside treeChanged handlers so it must read live Fluid state,
   * not a stale snapshot captured during React render.
   */
  getParticipantIds: () => string[];
  /**
   * Number of available seats at this location
   */
  seatCount: number;
}

interface AvatarSeatingResult {
  /**
   * Users that fit in the available seats (in join order)
   */
  seatedUsers: User[];
  /**
   * Users that don't fit in seats (overflow, in join order)
   */
  overflowUsers: User[];
}

interface ComputeAvatarSeatingParams {
  participantIds: string[];
  users: Users;
  seatCount: number;
}

function getAvailableSeats(userCount: number, seatCount: number): number {
  if (seatCount <= 0) {
    return 0;
  }

  // Reserve the last seat for the overflow indicator when there are more users than seats
  if (userCount > seatCount) {
    return seatCount - 1;
  }

  return seatCount;
}

function seatingUserArraysMatch(a: User[], b: User[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((user, i) => user.id === b.at(i)?.id);
}

function computeAvatarSeating({
  participantIds,
  users,
  seatCount,
}: ComputeAvatarSeatingParams): { seatedUsers: User[]; overflowUsers: User[] } {
  const usersById = new Map(users.map((user) => [user.id, user] as const));
  const seenParticipantIds = new Set<string>();
  const usersInOrder: User[] = [];

  for (const participantId of participantIds) {
    if (!participantId || seenParticipantIds.has(participantId)) {
      continue;
    }

    seenParticipantIds.add(participantId);

    const user = usersById.get(participantId);
    if (!user?.isOnline) {
      continue;
    }

    usersInOrder.push(user);
  }

  const availableSeats = getAvailableSeats(usersInOrder.length, seatCount);

  return {
    overflowUsers: usersInOrder.slice(availableSeats),
    seatedUsers: usersInOrder.slice(0, availableSeats),
  };
}

export function useAvatarSeating({
  getParticipantIds,
  seatCount,
  locationNode,
}: AvatarSeatingParams): AvatarSeatingResult {
  const { sharedTree } = useFluid();

  const [users, setUsers] = useState<{
    seatedUsers: User[];
    overflowUsers: User[];
  }>({ overflowUsers: [], seatedUsers: [] });

  // Keep a stable ref so the treeChanged handler always calls the latest getter
  const getParticipantIdsRef = useRef(getParticipantIds);
  getParticipantIdsRef.current = getParticipantIds;

  // Subscribe to location and audience changes
  useEffect(() => {
    const updateSeating = () => {
      const result = computeAvatarSeating({
        participantIds: getParticipantIdsRef.current(),
        seatCount,
        users: sharedTree.root.users,
      });

      // Prevent unnecessary re-renders when seating hasn't actually changed
      // (e.g. unrelated user property updates like presence/name trigger treeChanged)
      setUsers((prev) => {
        if (
          seatingUserArraysMatch(prev.seatedUsers, result.seatedUsers) &&
          seatingUserArraysMatch(prev.overflowUsers, result.overflowUsers)
        ) {
          return prev;
        }

        return result;
      });
    };

    // Subscribe to changes on the users array (for user online status, etc.)
    const unsubscribeUsers = Tree.on(
      sharedTree.root.users,
      "treeChanged",
      updateSeating
    );

    // Subscribe to changes on the location node (for participant list changes)
    const unsubscribeLocation = locationNode
      ? Tree.on(locationNode, "treeChanged", updateSeating)
      : undefined;

    // Initial computation after subscriptions are set up
    updateSeating();

    return () => {
      unsubscribeUsers();
      unsubscribeLocation?.();
    };
  }, [seatCount, sharedTree, locationNode]);

  return users;
}
